// "My Manuals" section content (BYOM): plan usage, upload flow, manuals list.
// Rendered inside the ManualsButton overlay; only reachable when logged in.
import { useCallback, useEffect, useState } from 'react';
import { listMyDocuments, getMyPlan } from './manualsApi';
import UploadManual from './UploadManual';
import ManualsList from './ManualsList';
import ChatView, { equipmentTitle } from '../chat/ChatView';
import MyNotes from './MyNotes';
import { BRAND } from '../brand';

export default function MyManuals() {
  const [documents, setDocuments] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When set, the section shows the chat for {equipmentPath, title}.
  const [chat, setChat] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [docs, planRow] = await Promise.all([listMyDocuments(), getMyPlan()]);
      setDocuments(docs);
      setPlan(planRow);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activeCount = documents.filter(d => d.status !== 'failed').length;

  if (chat) {
    return (
      <ChatView
        equipmentPath={chat.equipmentPath}
        title={chat.title}
        subtitle={chat.subtitle}
        onBack={() => setChat(null)}
      />
    );
  }

  return (
    <div>
      <p style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.65, margin: '0 0 18px' }}>
        Upload your own equipment manuals (PDF). {BRAND.name} indexes them privately for your
        account and uses them to answer your questions about that equipment.
      </p>

      {loading && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>Loading…</div>
      )}
      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      {!loading && (
        <>
          {plan && (
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                color: 'var(--text-3)',
                letterSpacing: 1,
                marginBottom: 14,
              }}
            >
              PLAN: {String(plan.plan).toUpperCase()} · {activeCount}/{plan.max_manuals} MANUAL
              SLOTS USED
            </div>
          )}

          {/* General maritime assistant — shared knowledge only, no manual needed */}
          <div
            className="panel"
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
          >
            <div style={{ flex: '1 1 220px' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                🌊 General Maritime Assistant
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
                Ask general marine engineering and regulatory questions — no manual required.
              </div>
            </div>
            <button
              className="btn"
              onClick={() =>
                setChat({ equipmentPath: 'general', title: 'General Maritime Assistant' })
              }
              style={{
                padding: '9px 18px',
                fontSize: 12,
                background: 'var(--primary)',
                color: '#ffffff',
              }}
            >
              💬 Chat
            </button>
          </div>

          <UploadManual plan={plan} manualCount={activeCount} onUploaded={refresh} />

          <div className="section-label">YOUR MANUALS ({documents.length})</div>
          <ManualsList
            documents={documents}
            onChanged={refresh}
            onChat={doc =>
              setChat({
                equipmentPath: doc.equipment_path,
                title: equipmentTitle(doc.equipment_path),
                subtitle: doc.filename,
              })
            }
          />

          {/* Private notebook — view/delete of the user's own notes (RLS) */}
          <MyNotes />
        </>
      )}
    </div>
  );
}
